import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Collapse, Dialog, IconButton, LinearProgress,
  Stack, TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AddRounded as AddIcon,
  BusinessOutlined as CompanyIcon,
  CloseRounded as CloseIcon,
  CloudUploadOutlined as DropIcon,
  DescriptionOutlined as TypeIcon,
  ImageOutlined as ImageIcon,
  LocalShippingOutlined as TruckIcon,
  PictureAsPdfOutlined as PdfIcon,
} from '@mui/icons-material';
import DocumentService, {
  DocumentOwnerType, RosterRow, UploadableType,
} from '../../api/services/document.service';
import { categoryLabel } from './vaultStatus';
import { formatBytes } from './vaultStatus';
import { transitions } from '../../motion';
import { radius } from '../../theme';

// Filing a document from the desk.
//
// This is the one place the web vault does something the phone cannot:
// **pick the owner**. On mobile the floating + can only file company-wide
// paperwork, and a driver's or a truck's document has to be filed from
// inside that driver's or truck's file. At a desk with a scanner and a
// stack of paper that is the wrong shape — so here you choose the type,
// then choose whose it is.
//
// Most of the time neither is a choice, though: you clicked a missing row
// in somebody's file, so the dialog already knows what and whose. It used
// to show that as two greyed-out form fields, which made a one-step job
// look like a form. Now what and whose is the HEADER, the file is the
// whole body, and the only fields left are the ones the paper itself
// answers — when it was issued and when it runs out.

// Mirrors the backend's middleware/uploadDocument.js. Checked here too so a
// 40MB scan or a .docx is refused before it spends a minute uploading.
const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [
  '.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif',
  '.csv', '.tsv', '.txt', '.json', '.xlsx', '.xls', '.docx', '.doc', '.mp4', '.mov', '.m4a', '.mp3',
];
export const ACCEPT = `${ALLOWED_EXTENSIONS.join(',')},application/pdf,image/*,video/*,audio/*`;

export function fileProblem(file: File): string | null {
  const name = file.name.toLowerCase();
  const typeOk = file.type === 'application/pdf' || file.type.startsWith('image/');
  if (!typeOk && !ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return 'That kind of file cannot be filed. PDFs, photos, CSV, Excel, Word, text, video and audio can.';
  }
  if (file.size > MAX_DOCUMENT_BYTES) {
    return `This file is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 25 MB.`;
  }
  return null;
}

// The filename the backend will generate — documentService.ownerToken and
// seeds/documentTypes.buildFileName, mirrored. Showing the template
// ("{owner}_{date}_Handbook") told nobody anything; showing the real name
// lets someone catch a wrong person or a wrong date before filing.
function ownerToken(ownerType: DocumentOwnerType, label?: string): string {
  if (!label) return '';
  if (ownerType === 'driver') {
    const parts = label.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[parts.length - 1]}${parts.slice(0, -1).join('')}`;
    return label.replace(/\s+/g, '');
  }
  const digits = label.match(/\d+/);
  return digits ? digits[0] : label.replace(/\s+/g, '');
}

function formatDocumentDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getUTCMonth() + 1).padStart(2, '0')}${String(d.getUTCDate()).padStart(2, '0')}${String(d.getUTCFullYear()).slice(-2)}`;
}

export function previewFileName(
  pattern: string,
  { ownerType, ownerLabel, issuedAt }: { ownerType: DocumentOwnerType; ownerLabel?: string; issuedAt?: string },
): string {
  const date = issuedAt || new Date().toISOString().slice(0, 10);
  const values: Record<string, string> = {
    '{date}': formatDocumentDate(date),
    '{owner}': ownerToken(ownerType, ownerLabel),
    '{vehicle}': ownerToken('vehicle', ownerLabel),
    '{driver}': ownerLabel || '',
  };
  return pattern.replace(/\{[a-z]+\}/g, (token) => values[token] ?? '').trim();
}

// First and last, so "Aidan Scott Poole" is AP, not AS.
const initials = (label: string) => {
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0]![0]!;
  const last = parts.length > 1 ? parts[parts.length - 1]![0]! : '';
  return `${first}${last}`.toUpperCase();
};

// ----------------------------------------------------------------- dialog

interface Props {
  open: boolean;
  onClose: () => void;
  onUploaded: (documentId: string) => void;
  // When opened from inside an entity file, the owner is already known and
  // the picker is locked to it.
  fixedOwner?: { ownerType: DocumentOwnerType; ownerId: string | null; label: string };
  // Preselects the type — a missing row, or "File renewal" on a document.
  presetTypeKey?: string;
}

const UploadDocumentDialog: React.FC<Props> = ({
  open, onClose, onUploaded, fixedOwner, presetTypeKey,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const [types, setTypes] = useState<UploadableType[]>([]);
  const [drivers, setDrivers] = useState<RosterRow[]>([]);
  const [vehicles, setVehicles] = useState<RosterRow[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [type, setType] = useState<UploadableType | null>(null);
  const [changingType, setChangingType] = useState(false);
  const [owner, setOwner] = useState<RosterRow | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [issuedAt, setIssuedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setType(null); setChangingType(false); setOwner(null); setFile(null);
    setIssuedAt(''); setExpiresAt(''); setNotes(''); setShowNotes(false);
    setError(null); setProgress(0);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    setLoadingOptions(true);
    // Rosters are only fetched to fill the picker; a failure there must not
    // stop a company-wide upload, so each one falls back to empty.
    Promise.all([
      DocumentService.getUploadableTypes(),
      DocumentService.getRoster('driver').catch(() => [] as RosterRow[]),
      DocumentService.getRoster('vehicle').catch(() => [] as RosterRow[]),
    ])
      .then(([typeList, driverList, vehicleList]) => {
        setTypes(typeList);
        if (presetTypeKey) setType(typeList.find((t) => t.key === presetTypeKey) ?? null);
        setDrivers(driverList);
        setVehicles(vehicleList);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the document types'))
      .finally(() => setLoadingOptions(false));
  }, [open, reset, presetTypeKey]);

  // With the owner locked, only that owner's kind of paper is offered — a
  // truck's inspection must never be filed with a driver's id. Sorted by
  // category because Autocomplete's groupBy only groups ADJACENT options.
  const typeOptions = useMemo(
    () => types
      .filter((t) => !fixedOwner || t.ownerType === fixedOwner.ownerType)
      .sort((a, b) => categoryLabel(a.category).localeCompare(categoryLabel(b.category))
        || a.label.localeCompare(b.label)),
    [types, fixedOwner],
  );

  // Which owners are selectable follows from the TYPE: an MEC belongs to a
  // person, an inspection to a truck.
  const ownerOptions = useMemo(() => {
    if (!type) return [];
    if (type.ownerType === 'driver') return drivers;
    if (type.ownerType === 'vehicle') return vehicles;
    return [];
  }, [type, drivers, vehicles]);

  const needsOwner = !!type && (type.ownerType === 'driver' || type.ownerType === 'vehicle') && !fixedOwner;
  const expires = !!type?.expiry;
  // The backend refuses a 'continuous' type (a licence, a COI) without an
  // expiry date, because there would be nothing to track.
  const expiryRequired = type?.expiry?.kind === 'continuous';
  const problem = file ? fileProblem(file) : null;

  // "Headed" = the dialog already knows what this is, so it is the title
  // rather than a question. Opening from the page header asks instead.
  const headed = !!type && !!presetTypeKey && !changingType;

  const whose = fixedOwner
    ? { ownerType: fixedOwner.ownerType, label: fixedOwner.label }
    : owner
      ? { ownerType: owner.ownerType, label: owner.label }
      : type?.ownerType === 'company' ? { ownerType: 'company' as const, label: 'Company-wide' } : null;

  const fileName = type && (!needsOwner || owner)
    ? previewFileName(type.namePattern, {
      ownerType: type.ownerType,
      ownerLabel: fixedOwner ? fixedOwner.label : owner?.label,
      issuedAt,
    })
    : null;

  const canSubmit = !!type && !!file && !problem
    && (!needsOwner || !!owner) && (!expiryRequired || !!expiresAt) && !saving;

  const submit = async () => {
    if (!type || !file) return;
    setSaving(true);
    setProgress(0);
    setError(null);
    try {
      const document = await DocumentService.upload({
        documentTypeKey: type.key,
        ownerId: fixedOwner ? fixedOwner.ownerId : owner?.ownerId ?? null,
        ownerLabel: fixedOwner ? fixedOwner.label : owner?.label,
        issuedAt: issuedAt ? new Date(issuedAt).toISOString() : undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
        notes: notes.trim() || undefined,
        file,
      }, setProgress);
      onUploaded(document._id);
    } catch (err) {
      // Surfaced verbatim: the backend refuses a replacement whose expiry is
      // not later than the one on file, and that message explains a real
      // problem the person needs to act on.
      setError(err instanceof Error ? err.message : 'Could not file this document');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
      // The theme rounds every dialog; a full-screen sheet with rounded
      // corners floats a few pixels off the edges of the phone.
      PaperProps={{ sx: isPhone ? { borderRadius: 0 } : undefined }}
      aria-labelledby="file-document-title"
    >
      {/* --------------------------------------------------------- header */}
      <Stack direction="row" gap={2} alignItems="flex-start" sx={{ px: 3, pt: 3, pb: 2.5 }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: `${radius.lg}px`, flexShrink: 0,
            display: 'grid', placeItems: 'center',
            bgcolor: 'primary.light', color: 'primary.dark',
          }}
        >
          <TypeIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          <Typography id="file-document-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {headed ? type!.label : 'File a document'}
          </Typography>
          {headed ? (
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
              {whose && <OwnerTag ownerType={whose.ownerType} label={whose.label} />}
              <Typography variant="body2" color="text.secondary">
                {categoryLabel(type!.category)}
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={() => setChangingType(true)}
                sx={{
                  border: 0, bgcolor: 'transparent', p: 0, font: 'inherit', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, color: 'primary.main',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Change
              </Box>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {fixedOwner ? `For ${fixedOwner.label}. ` : ''}Pick what it is, then add the file.
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Close" onClick={onClose} disabled={saving} sx={{ mt: -0.5, mr: -1 }}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* ----------------------------------------------------------- body */}
      {/* pt: room for the first field's floating label, which this
          scrolling body would otherwise clip. */}
      <Stack gap={2.5} sx={{ px: 3, pt: 1.25, pb: 3, flex: 1, overflowY: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {!headed && (
          <Autocomplete
            options={typeOptions}
            loading={loadingOptions}
            value={type}
            onChange={(_, value) => { setType(value); setOwner(null); }}
            groupBy={(option) => categoryLabel(option.category)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a.key === b.key}
            // People search for "MEC" and "med card", not "Medical
            // Examiner's Certificate" — the catalog carries those aliases.
            filterOptions={(options, { inputValue }) => {
              const term = inputValue.trim().toLowerCase();
              if (!term) return options;
              return options.filter((option) =>
                option.label.toLowerCase().includes(term)
                || option.key.toLowerCase().includes(term)
                || (option.aliases || []).some((alias) => alias.toLowerCase().includes(term)));
            }}
            renderInput={(params) => (
              <TextField {...params} label="What is it?" placeholder="Search — e.g. MEC, insurance, W-4" />
            )}
          />
        )}

        {needsOwner && (
          <Autocomplete
            options={ownerOptions}
            value={owner}
            onChange={(_, value) => setOwner(value)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a.ownerId === b.ownerId}
            renderOption={(props, option) => (
              <li {...props} key={option.ownerId}>
                <Stack direction="row" gap={1.5} alignItems="center">
                  <OwnerAvatar ownerType={option.ownerType} label={option.label} size={28} />
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    {option.sublabel && (
                      <Typography variant="caption" color="text.secondary">{option.sublabel}</Typography>
                    )}
                  </Box>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label={type?.ownerType === 'vehicle' ? 'Which vehicle?' : 'Whose is it?'} />
            )}
          />
        )}

        <FileDrop file={file} problem={problem} disabled={saving} onFile={setFile} />

        {type && (
          <Box
            sx={{
              display: 'grid', gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: expires ? '1fr 1fr' : '1fr' },
            }}
          >
            <TextField
              label="Issued"
              type="date"
              value={issuedAt}
              onChange={(e) => setIssuedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Used in the file name"
              fullWidth
            />
            {/* Hidden entirely when this kind of paper never expires. An
                empty expiry field on an I-9 invites someone to invent one. */}
            {expires && (
              <TextField
                label="Expires"
                type="date"
                required={expiryRequired}
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={expiryRequired ? 'As printed on the document' : 'If the document states one'}
                fullWidth
              />
            )}
          </Box>
        )}

        {type && (
          <Box>
            <Collapse in={!showNotes} unmountOnExit>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setShowNotes(true)} sx={{ ml: -0.75 }}>
                Add a note
              </Button>
            </Collapse>
            <Collapse in={showNotes} unmountOnExit>
              <TextField
                label="Note"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                minRows={2}
                autoFocus
                fullWidth
              />
            </Collapse>
          </Box>
        )}
      </Stack>

      {/* --------------------------------------------------------- footer */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', position: 'relative' }}>
        {saving && (
          <LinearProgress
            variant={progress > 0 && progress < 1 ? 'determinate' : 'indeterminate'}
            value={progress * 100}
            sx={{ position: 'absolute', top: -1, left: 0, right: 0, height: 2, borderRadius: 0 }}
          />
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={2}
          sx={{ px: 3, py: 2 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {fileName && (
              <>
                <Typography variant="caption" color="text.secondary" display="block">
                  Will be saved as
                </Typography>
                <Tooltip title={fileName}>
                  <Typography
                    noWrap
                    sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}
                  >
                    {fileName}
                  </Typography>
                </Tooltip>
              </>
            )}
          </Box>
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="contained" disableElevation onClick={submit} disabled={!canSubmit} sx={{ minWidth: 140 }}>
              {saving
                ? (progress > 0 && progress < 1 ? `Filing… ${Math.round(progress * 100)}%` : 'Filing…')
                : 'File document'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
};

// ------------------------------------------------------------ the owner

const OwnerAvatar: React.FC<{ ownerType: DocumentOwnerType; label: string; size?: number }> = ({
  ownerType, label, size = 22,
}) => (
  <Avatar
    sx={{
      width: size, height: size, fontSize: size * 0.42, fontWeight: 700,
      bgcolor: 'grey.200', color: 'text.primary',
      '& svg': { fontSize: size * 0.62 },
    }}
  >
    {ownerType === 'vehicle' ? <TruckIcon /> : ownerType === 'company' ? <CompanyIcon /> : initials(label)}
  </Avatar>
);

const OwnerTag: React.FC<{ ownerType: DocumentOwnerType; label: string }> = ({ ownerType, label }) => (
  <Stack
    direction="row"
    gap={0.75}
    alignItems="center"
    sx={{ pl: 0.25, pr: 1, py: 0.25, borderRadius: 999, bgcolor: 'grey.100' }}
  >
    <OwnerAvatar ownerType={ownerType} label={label} />
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
  </Stack>
);

// --------------------------------------------------------------- the file

// The whole point of the dialog, so it is the biggest thing in it: a drop
// target you can also click, which becomes a preview of what you chose.
const FileDrop: React.FC<{
  file: File | null;
  problem: string | null;
  disabled?: boolean;
  onFile: (file: File | null) => void;
}> = ({ file, problem, disabled, onFile }) => {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  // An object URL per chosen image, revoked when it changes — HEIC fails
  // to decode in most browsers and falls back to the glyph.
  useEffect(() => {
    setPreviewFailed(false);
    if (!file || !file.type.startsWith('image/')) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const browse = () => input.current?.click();
  const isPdf = !!file && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));

  const dropHandlers = {
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); if (!disabled) setDragging(true); },
    onDragLeave: () => setDragging(false),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (!disabled && e.dataTransfer.files?.[0]) onFile(e.dataTransfer.files[0]);
    },
  };

  const hidden = (
    <input
      ref={input}
      hidden
      type="file"
      accept={ACCEPT}
      data-testid="document-file-input"
      onChange={(e) => {
        onFile(e.target.files?.[0] ?? null);
        // Lets the same file be chosen again after it was removed.
        e.target.value = '';
      }}
    />
  );

  if (file) {
    return (
      <Box {...dropHandlers}>
        {hidden}
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          sx={{
            p: 1.5, borderRadius: `${radius.lg}px`, border: '1px solid',
            borderColor: problem ? 'error.main' : dragging ? 'primary.main' : 'divider',
            bgcolor: problem ? 'error.light' : 'background.paper',
            transition: transitions(['border-color', 'background-color']),
          }}
        >
          <Box
            sx={{
              width: 56, height: 56, borderRadius: `${radius.md}px`, flexShrink: 0, overflow: 'hidden',
              display: 'grid', placeItems: 'center',
              bgcolor: isPdf ? 'error.light' : 'primary.light',
              color: isPdf ? 'error.main' : 'primary.main',
            }}
          >
            {preview && !previewFailed ? (
              <Box
                component="img"
                src={preview}
                alt=""
                onError={() => setPreviewFailed(true)}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : isPdf ? <PdfIcon /> : <ImageIcon />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{file.name}</Typography>
            <Typography variant="caption" color={problem ? 'error.main' : 'text.secondary'}>
              {problem || `${formatBytes(file.size)} · ${isPdf ? 'PDF' : 'Image'}`}
            </Typography>
          </Box>
          <Button size="small" onClick={browse} disabled={disabled} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
            Replace
          </Button>
          <IconButton size="small" aria-label="Remove file" onClick={() => onFile(null)} disabled={disabled}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={browse}
      disabled={disabled}
      {...dropHandlers}
      sx={{
        width: '100%', font: 'inherit', color: 'inherit', cursor: 'pointer',
        px: 3, py: 4, borderRadius: `${radius.xl}px`,
        border: '1.5px dashed',
        borderColor: dragging ? 'primary.main' : 'grey.300',
        bgcolor: dragging ? 'primary.light' : 'grey.50',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        transition: transitions(['border-color', 'background-color']),
        '&:hover': { borderColor: 'primary.main' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      {hidden}
      <Box
        sx={{
          width: 48, height: 48, borderRadius: '50%', display: 'grid', placeItems: 'center',
          bgcolor: 'background.paper', color: 'primary.main',
          boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 0 0 1px var(--mui-palette-divider)',
        }}
      >
        <DropIcon />
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
        {dragging ? 'Drop it here' : (
          <>Drop the file here, or <Box component="span" sx={{ color: 'primary.main' }}>browse</Box></>
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        PDF, JPG, PNG or HEIC · up to 25 MB
      </Typography>
    </Box>
  );
};

export default UploadDocumentDialog;
